import { useMemo } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { skipToken, useMutation, useQuery } from '@tanstack/react-query';

import { useActiveTurnId } from '@/apis/v2/chat';
import {
  globalOutputsDict,
  registerHtmlArtifactsFromFunctionPayload,
  registerImageUrlsFromFunctionPayload
} from '@/components/chat/message_display_util';
import {
  conversationDraftKey,
  conversationMessagesKey,
  draftRepresentedIn,
  mergeTurnRowsIntoHistory
} from '@/lib/chat/message-cache';
import { type TurnStreamData, turnEventsKey } from '@/lib/chat/turn-stream';
import { useUserInformation } from '@/lib/hooks/react-query/user';
import { Message } from '@/lib/types/chat';
import { UserData } from '@/lib/types/user';

import {
  downloadConversationFilesArchive,
  getConversationFiles,
  getMessagesByConversationId
} from './api';
import { useChatAPI } from './chat_api';

const updateGlobalDictForMessage = (message: Message) => {
  if (message.role === 'function') {
    try {
      const functionPayload = JSON.parse(message.content);

      if (functionPayload.outputs_dict) {
        Object.entries(functionPayload.outputs_dict).forEach(([key, value]) => {
          globalOutputsDict[key] = value;
        });
      }
      registerHtmlArtifactsFromFunctionPayload(
        functionPayload,
        message.message_id ?? message.id ?? null
      );

      registerImageUrlsFromFunctionPayload(functionPayload);
    } catch (err) {
      console.error(err);
    }
  }
};

export const conversationMessagesQueryFn = async (
  conversationId: string,
  accessToken: string,
  userPreferences: UserData['preferences'] = undefined
) => {
  let messages = await getMessagesByConversationId(conversationId, accessToken);

  messages.forEach(updateGlobalDictForMessage);

  if (userPreferences?.hide_code)
    messages = messages.map((message) => ({ ...message, isCollapsed: true }));

  return messages;
};

export const useConversationMessages = <T = Partial<Message>[]>(
  conversationId: string,
  options?: {
    enabled?: boolean;
    // Narrowed subscriptions (one cell's rows, a role filter) without a
    // second query entry; structural sharing keeps re-renders scoped.
    select?: (rows: Partial<Message>[]) => T;
  }
) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { data: userData } = useUserInformation();

  // const isLoading =
  //   authLoading || (!authLoading && isAuthenticated && !userData && userDataLoading);

  return useQuery<Partial<Message>[], Error, T>({
    queryKey: conversationMessagesKey(conversationId),
    queryFn: async () => {
      let accessToken = '';
      if (isAuthenticated) accessToken = await getAccessTokenSilently();

      // So the problem is, it's possible that the user is not logged in, but can
      // request messages. In that case, we don't care about user preferences.
      // But if they are logged in, we want to use their preferences
      const data = isAuthenticated ? userData?.preferences : undefined;
      return conversationMessagesQueryFn(conversationId, accessToken, data);
    },
    enabled: !!conversationId && (options?.enabled ?? true),
    staleTime: Infinity,
    // Default gcTime: live streams write the turn-events entry (whose
    // headless subscriber holds an observer for the whole run), and the
    // settle fold no-ops when an unobserved history entry was evicted — the
    // rows are server-persisted by then.
    // One transient failure on a deep link must not blank history for the
    // whole session — staleTime Infinity means an errored load never retries
    // on its own.
    retry: 2,
    // Self-heal an errored load on focus without ever refetching successful
    // (stream-written) caches, which the streamed rows must outlive.
    refetchOnWindowFocus: (query) => query.state.status === 'error',
    select: options?.select
  });
};

// The canonical read path for anything that must see the LIVE turn: union of
// persisted history (['conversation/messages']) and the active turn's
// streamed rows (['conversation/turn-events']), deduped by
// mergeTurnRowsIntoHistory — the same rule the settle fold applies, so
// nothing double-renders across the fold tick. No turn entry -> passthrough
// of history, which is what keeps replay/demo/workflow untouched (they write
// history directly). Union, never turn-only: replay streams
// partially-built rows INTO history and those must survive the merge.
export const useMergedConversationMessages = <T = Partial<Message>[]>(
  conversationId: string,
  options?: {
    enabled?: boolean;
    // Applied AFTER the merge. Pass a stable reference — an inline lambda
    // re-runs (and re-allocates) every render.
    select?: (rows: Partial<Message>[]) => T;
  }
) => {
  const historyQuery = useConversationMessages(conversationId, { enabled: options?.enabled });
  const activeTurnId = useActiveTurnId(conversationId);

  // Observes the turn entry WITHOUT owning it: skipToken means this query
  // never fetches (the headless TurnStreamSubscriber owns the streamedQuery)
  // and, unlike a bare enabled:false with no queryFn, doesn't trip RQ's
  // dev-mode missing-queryFn warning — while still subscribing to cache
  // writes for its key, so every reducer setQueryData notifies readers here.
  // gcTime 0 keeps the idle sentinel key (no active turn) from lingering;
  // live entries are governed by the subscriber's gcTime, since a query's
  // gcTime only ratchets up.
  const { data: turnEntry } = useQuery<TurnStreamData>({
    queryKey: turnEventsKey(conversationId ?? '', activeTurnId ?? ''),
    queryFn: skipToken,
    gcTime: 0
  });

  // The send's in-flight user message. Client-owned key, never fetched;
  // existence IS the gate (the bridge isn't reachable here, and a liveness
  // gate would blank the message during the persist round-trip). gcTime
  // Infinity: removal is explicit (settle/failure), never gc's business.
  const { data: draft } = useQuery<Partial<Message>>({
    queryKey: conversationDraftKey(conversationId ?? ''),
    queryFn: skipToken,
    gcTime: Infinity
  });

  const historyRows = historyQuery.data;
  const turnRows = turnEntry?.rows;
  const select = options?.select;

  // Merge and select in ONE memo keyed on exactly the inputs that can change
  // the output. This is also the stability story: the no-turn/no-draft
  // passthrough returns historyRows BY REFERENCE, and primitive select
  // outputs are value-stable in downstream deps (Object.is), so per-chunk
  // churn is confined to hosts observing a live turn. The chain is COMPLETE:
  // persisted history ∪ live turn rows ∪ the send draft — view mode is a
  // pure derivation of length, and no consumer needs to know the tiers.
  const data = useMemo(() => {
    // The draft joins the HISTORY side BEFORE turn rows merge: streamed
    // assistant rows must append AFTER the question they answer, or the
    // whole streaming phase renders above/into the previous turn's group.
    const base =
      draft?.role === 'user' && !(historyRows && draftRepresentedIn(historyRows, draft))
        ? [...(historyRows ?? []), draft]
        : historyRows;
    const merged = turnRows?.length ? mergeTurnRowsIntoHistory(base ?? [], turnRows) : base;
    return (select && merged ? select(merged) : merged) as T | undefined;
  }, [historyRows, turnRows, draft, select]);

  return { ...historyQuery, data };
};

export const useConversationFiles = (conversationId: string | null, enabled = true) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  return useQuery({
    queryKey: ['conversation/files', conversationId],
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      return getConversationFiles(conversationId, accessToken);
    },
    enabled: enabled && isAuthenticated && Boolean(conversationId),
    retry: false,
    refetchOnWindowFocus: false
  });
};

export const useDownloadConversationFilesMutation = (conversationId: string) => {
  const { getAccessTokenSilently } = useAuth0();

  return useMutation({
    mutationFn: async () => {
      const accessToken = await getAccessTokenSilently();
      return downloadConversationFilesArchive(conversationId, accessToken);
    }
  });
};

export const useGeneratedDataframePreviewQuery = (
  filename: string | null,
  conversationId: string | null = null
) => {
  const { getSpreadsheetPreview } = useChatAPI();
  const extension = filename?.split('.').pop()?.toLowerCase() ?? '';

  return useQuery({
    queryKey: ['generated-dataframe-preview', filename, conversationId],
    enabled: !!filename,
    staleTime: 5 * 60 * 1000,
    queryFn: () => getSpreadsheetPreview({ filename: filename, extension, conversationId })
  });
};
